import { getLocale } from '@/core/settings/utils';
import {
  PRE_ORDER_SCHEDULED_DELIVERY_AT_STREET,
  PRE_ORDER_SCHEDULED_DELIVERY_ROOM_OF_CHOICE,
} from '@/productDelivery/DeliveryPromisesList/constants';
import { DeliveryOffer } from '@/productDiscovery/ProductPage/common/modules/Delivery/delivery.ducks';
import {
  beforeTimeDelivery,
  betweenDatesDeliveryWithDiffMonth,
  betweenDatesDeliveryWithDiffYear,
  betweenDatesDeliveryWithSameMonth,
  onDateDelivery,
  preOrderScheduledDelivery,
} from '@/productDiscovery/ProductPage/common/modules/Delivery/translations';
import { cutOffParser } from '@/productDiscovery/ProductPage/common/modules/Delivery/Utils/getCutOffParser';

import { getDeliveryMode } from './getDeliveryMode';
import { formatOrdinalsDate } from './getPluralDate';

export const getDeliveryWording = (offer: DeliveryOffer) => {
  const { minDeliveryTime, maxDeliveryTime, type, orderBefore, expressMode } =
    offer;
  const dateMax = new Date(Date.now() + maxDeliveryTime * 24 * 3600 * 1000);
  const dateMin = new Date(Date.now() + minDeliveryTime * 24 * 3600 * 1000);
  const monthMin = dateMin.getMonth();
  const monthMax = dateMax.getMonth();
  const dayMin = formatOrdinalsDate(dateMin.getDate());
  const dayMax = formatOrdinalsDate(dateMax.getDate());
  const yearMax = dateMax.getFullYear();
  const yearMin = dateMin.getFullYear();
  const currentYear = new Date().getFullYear();
  const monthMaxFormat = new Intl.DateTimeFormat(getLocale(), {
    month: 'long',
  }).format(dateMax);
  const monthMinFormat = new Intl.DateTimeFormat(getLocale(), {
    month: 'long',
  }).format(dateMin);

  const isSameDay = minDeliveryTime === maxDeliveryTime;

  const isPreOrderScheduledDelivery =
    type === PRE_ORDER_SCHEDULED_DELIVERY_AT_STREET ||
    type === PRE_ORDER_SCHEDULED_DELIVERY_ROOM_OF_CHOICE;

  const deliveryWordingMode = getDeliveryMode(type, expressMode);

  let orderBeforeWording = '';
  let deliveryWording = '';

  if (isSameDay) {
    // On 12 February
    deliveryWording = `${onDateDelivery.format({
      day: dayMax,
      month: monthMaxFormat,
    })}`;
  } else if (yearMax !== currentYear) {
    // Between 12 February 2024 and 12 February 2025
    deliveryWording = `${betweenDatesDeliveryWithDiffYear.format({
      dayMin,
      dayMax,
      monthMin: monthMinFormat,
      monthMax: monthMaxFormat,
      yearMin,
      yearMax,
    })}`;
  } else if (monthMin === monthMax) {
    // Between 12 and 15 February
    deliveryWording = `${betweenDatesDeliveryWithSameMonth.format({
      dayMin,
      dayMax,
      monthMax: monthMaxFormat,
    })}`;
  } else {
    // Between 12 February and 15 March
    deliveryWording = `${betweenDatesDeliveryWithDiffMonth.format({
      dayMin,
      dayMax,
      monthMin: monthMinFormat,
      monthMax: monthMaxFormat,
    })}`;
  }

  if (isPreOrderScheduledDelivery) {
    deliveryWording = preOrderScheduledDelivery.format({
      fromDay: Number(dayMin),
      fromMonth: monthMinFormat,
    }) as string;
  }

  if (orderBefore) {
    const { hours, minutes } = cutOffParser(orderBefore);
    orderBeforeWording = `${beforeTimeDelivery.format({
      hours,
      minutes,
    })}`;
  }

  return {
    deliveryWordingMode,
    deliveryWording,
    orderBefore,
    orderBeforeWording,
  };
};
